// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/components/DesignLink.tsx
// PLAN2 WP09 — C-105: the design package cannot import a router. Navigational components render DesignLink, which reads
// the link component from context: a plain <a> by default (Storybook, the site), Remix <Link prefetch="intent"> when the
// console provides it (WP10 root). Ported from piconameconsole/packages/design/src/link.tsx (renamed, extended with the
// external-link rule).
import { createContext, forwardRef, useContext, type AnchorHTMLAttributes, type ComponentType, type ReactNode, type Ref } from "react";
import { S } from "../strings.js";
import { useDesignI18n } from "../i18n.js";

export type LinkComponentProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "style"> & { href: string; children?: ReactNode; "data-testid"?: string };
export type LinkComponent = ComponentType<LinkComponentProps & { ref?: Ref<HTMLAnchorElement> }>;

const Anchor = forwardRef<HTMLAnchorElement, LinkComponentProps>(function Anchor({ href, children, ...rest }, ref) {
  return <a ref={ref} href={href} {...rest}>{children}</a>;
});
const LinkContext = createContext<LinkComponent>(Anchor as LinkComponent);

export function DesignLinkProvider({ component, children }: { component: LinkComponent; children: ReactNode }) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}
export function useLinkComponent(): LinkComponent {
  return useContext(LinkContext);
}

export const DesignLink = forwardRef<HTMLAnchorElement, LinkComponentProps>(function DesignLink({ href, children, target, rel, ...rest }, ref) {
  const Component = useLinkComponent();
  const { t } = useDesignI18n();
  if (/^https?:\/\//.test(href)) {
    const newTab = target === "_blank";
    return (
      <a ref={ref} href={href} target={target} rel={newTab ? "noopener noreferrer" : rel} {...rest}>
        {children}
        {newTab ? <span className="sr-only">{` ${t(S.a11yExternal)}`}</span> : null}
      </a>
    );
  }
  return <Component ref={ref} href={href} target={target} rel={rel} {...rest}>{children}</Component>;
});
